import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, API_BASE } from "../../lib/api";

/**
 * VoiceTextInput — Whisper-powered "speak instead of type" wrapper for textareas.
 *
 * Props:
 *  - value, onChange:           controlled textarea text
 *  - rows, placeholder, maxLength: forwarded to <textarea>
 *  - language:                  ISO 639-1 code passed to Whisper for accuracy
 *  - callerContext:             "onboarding" → admin-subsidized billing
 *  - maxSeconds:                hard cap on recording length (default 120s)
 *  - testid:                    data-testid prefix for the wrapper
 */
export default function VoiceTextInput({
  value,
  onChange,
  rows = 5,
  placeholder = "",
  maxLength = 500,
  language = "en",
  callerContext = "",
  maxSeconds = 120,
  testid = "voice-text-input",
}) {
  const { t } = useTranslation("voice");
  const [mode, setMode] = useState("idle"); // idle | recording | transcribing
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Cleanup on unmount.
  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        try { mediaRecorderRef.current.stop(); } catch { /* ignore */ }
      }
    },
    [],
  );

  const startRecording = async () => {
    if (mode !== "idle") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        handleStop();
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setMode("recording");
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((s) => {
          if (s + 1 >= maxSeconds) {
            // Auto-stop at the cap.
            try { mr.stop(); } catch { /* ignore */ }
            clearInterval(timerRef.current);
            return maxSeconds;
          }
          return s + 1;
        });
      }, 1000);
    } catch (err) {
      console.error(err);
      toast.error(t("voice_input.mic_denied", "Microphone access denied"));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleStop = async () => {
    setMode("transcribing");
    try {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      const form = new FormData();
      form.append("audio", blob, "speech.webm");
      form.append("language", language);
      if (callerContext) form.append("caller_context", callerContext);
      const { data } = await api.post("/voice/turn-stt", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const text = (data?.text || "").trim();
      if (!text) {
        toast.message(t("voice_input.empty", "Didn't catch anything — try again."));
        return;
      }
      // Append to existing value with a single space if there's content already.
      const merged = value ? value.trimEnd() + " " + text : text;
      onChange(maxLength ? merged.slice(0, maxLength) : merged);
      toast.success(t("voice_input.added", "Transcripción agregada"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "onboarding_voice_quota_exhausted") {
        toast.error(t("voice_input.quota", "Onboarding voice quota reached. Please type."));
      } else if (detail?.code === "admin_daily_cap_reached") {
        toast.error(t("voice_input.daily_cap", "Voice input unavailable right now."));
      } else {
        toast.error(t("voice_input.failed", "Transcription failed. Try again."));
      }
    } finally {
      setMode("idle");
      setElapsed(0);
    }
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const disabled = mode === "transcribing";
  const counterPct = Math.min(100, Math.round((elapsed / maxSeconds) * 100));

  return (
    <div className="relative" data-testid={testid}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-sm border border-input bg-background px-3 py-2 pr-12 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
        data-testid={`${testid}-textarea`}
      />
      <button
        type="button"
        onClick={mode === "recording" ? stopRecording : startRecording}
        disabled={disabled}
        title={
          mode === "recording"
            ? t("voice_input.stop", "Detener")
            : t("voice_input.start", "Hablar para escribir")
        }
        className={
          "absolute top-2 right-2 inline-flex h-8 w-8 items-center justify-center rounded-sm transition-colors " +
          (mode === "recording"
            ? "bg-red-500 hover:bg-red-600 text-white animate-pulse"
            : "bg-secondary hover:bg-secondary/80 text-foreground")
        }
        data-testid={`${testid}-mic`}
      >
        {mode === "transcribing" ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : mode === "recording" ? (
          <Square className="h-4 w-4 fill-current" />
        ) : (
          <Mic className="h-4 w-4" />
        )}
      </button>

      <div className="mt-1 flex items-center justify-between text-[11px] font-mono">
        <span className="text-muted-foreground">
          {mode === "recording" && (
            <span className="text-red-500" data-testid={`${testid}-status`}>
              {t("voice_input.recording", "Grabando")} · {mmss(elapsed)} / {mmss(maxSeconds)}
            </span>
          )}
          {mode === "transcribing" && (
            <span className="text-primary" data-testid={`${testid}-status`}>
              {t("voice_input.transcribing", "Transcribiendo…")}
            </span>
          )}
          {mode === "idle" && callerContext === "onboarding" && (
            <span className="text-muted-foreground">
              {t("voice_input.free_hint", "Hablá si preferís grabar · gratis durante el onboarding")}
            </span>
          )}
        </span>
        <span className="text-muted-foreground">{value.length}/{maxLength}</span>
      </div>

      {mode === "recording" && (
        <div className="mt-1 h-0.5 w-full bg-secondary overflow-hidden rounded-sm">
          <div
            className="h-full bg-red-500 transition-all"
            style={{ width: `${counterPct}%` }}
          />
        </div>
      )}
    </div>
  );
}

// API_BASE is needed for raw fetch fallback in environments where axios doesn't carry cookies.
// We use the existing `api` axios instance which already attaches the Bearer token.
export { API_BASE };
